import React, { useCallback, useEffect } from 'react';
import { Device } from '@twilio/voice-sdk';
import axios from 'axios';
import { isEmpty, isNil } from 'lodash';

import { ClaimContextProvider } from '~/components/ClaimContainer';
import { getContact } from '~/components/Contact';
import { MinimizedDialogContextProvider } from '~/components/core/MinimizedBar/Context';
import { useCms } from '~/components/hooks/useCms';
import LoadingDialog from '~/components/LoadingDialog';
import { useFetchClaim } from '~/hooks/useFetchClaim';
import { reportAxiosError, reportTwilioError } from '~/Utils';

import CallInProgressCard from './components/CallInProgressCard';
import { TWILIO_ERRORS_TO_IGNORE } from './components/CmsCallCenter';
import { CommunicationCallProvider, useCommunicationCallContext } from './Context/CommunicationCallContext';
import { attachTokenRefreshHandler, TOKEN_REFRESH_MS } from './twilioDeviceToken';

const OutgoingCallInProgressContainer = () => (
  <CommunicationCallProvider>
    <MinimizedDialogContextProvider>
      <OutgoingCallInProgressContainerInner />
    </MinimizedDialogContextProvider>
  </CommunicationCallProvider>
);

export const OutgoingCallInProgressContainerInner = () => {
  const deviceRef = React.useRef(undefined);
  const communicationIdRef = React.useRef(undefined);
  const offlineErrorRef = React.useRef(false);
  const communicationCreatedRef = React.useRef(false);

  const { user, userOrganization, callInProgress, setCallInProgress, userReloadTwilioDetails } = useCms();
  const {
    contactId,
    contactPhoneId,
    claimId,
    content,
    summary,
    exposureIds,
    phoneNumber,
    isNotClaimRelated,
    claimToAttachId,
    categoryId,
  } = callInProgress;
  const [claim, isLoadingClaim, isErrorLoadingClaim, reloadClaim] = useFetchClaim(claimId || claimToAttachId);

  const { callStatus, communication, updateCallConnection, updateCommunication, updateDisconnectOrCancel } =
    useCommunicationCallContext();

  const communicationId = communication && communication.id;
  async function fetchAccessToken() {
    const res = await axios.get('/api/v1/calls/twilio_outgoing_access_token');

    return res.data;
  }

  const handleIncomingCall = () => {
    throw Error('Outgoing calls should not accept calls');
  };

  const handleCallAccepted = React.useCallback(
    async (call) => {
      updateCallConnection(call);
      userReloadTwilioDetails();

      if (communicationIdRef.current) {
        const communicationRes = await axios.get(`/api/v1/communications/${communicationIdRef.current}`);
        updateCommunication(communicationRes.data); // If the communication was updated after the call initialized (e.g recording started)
      }
    },
    [userReloadTwilioDetails, updateCallConnection, updateCommunication]
  );

  const handleDisconnectOrCancel = React.useCallback(
    (call) => {
      updateDisconnectOrCancel(call);

      deviceRef.current.destroy();
      setTimeout(userReloadTwilioDetails, 5000); // So the status would be updated
    },
    [userReloadTwilioDetails, updateDisconnectOrCancel]
  );

  const handleCallFinished = useCallback(() => {
    setCallInProgress(null);
    userReloadTwilioDetails();
  }, [userReloadTwilioDetails, setCallInProgress]);

  const handleOffline = useCallback(() => {
    if (offlineErrorRef.current) {
      return;
    }
    offlineErrorRef.current = true;
  }, []);

  useEffect(() => {
    communicationIdRef.current = communicationId;
  }, [communicationId]);

  useEffect(() => {
    async function callContact(communication) {
      if (!deviceRef.current) {
        return;
      }

      const call = await deviceRef.current.connect({
        params: {
          CmsUserId: user.id,
          CmsClaimId: claimId,
          CmsCommunicationId: communication.id,
          OutgoingFeCall: true,
        },
      });

      // Set up call-specific event listeners
      call.on('accept', handleCallAccepted);
      call.on('disconnect', handleDisconnectOrCancel);
      call.on('cancel', handleDisconnectOrCancel);

      updateCallConnection(call);
    }

    const createCommunication = async () => {
      const result = await axios.post('/api/v1/communications/phone_calls', {
        direction: 'Outgoing',
        is_no_answer: true,
        phone_number: phoneNumber,
        content,
        summary,
        category_id: categoryId,
      });

      updateCommunication(result.data);

      return result.data;
    };

    const createClaimCommunication = async (contact) => {
      const result = await axios.post(
        `/api/v1/claims/${claimId}/contacts/${contact.id}/phones/${
          contactPhoneId ? contactPhoneId : contact.primary_phone.id
        }/communications`,
        {
          direction: 'Outgoing',
          is_no_answer: true,
          exposure_ids: exposureIds,
          content,
          summary,
          category_id: categoryId,
        }
      );

      updateCommunication(result.data);

      return result.data;
    };

    async function handleReady(device) {
      if (communicationCreatedRef.current) {
        return; //  handleReady may be called again if connection disconnected, we don't want to create communication again
      }

      try {
        deviceRef.current = device;
        let communication;
        if (isNotClaimRelated) {
          communication = await createCommunication();
        } else {
          const contact = await getContact(contactId, null, userOrganization.id);
          // getContact returns undefined instead of throwing when it refuses to fetch (missing
          // contactId or orgId). Name that here rather than letting createClaimCommunication
          // dereference undefined, so the reported failure is the missing contact and not the deref.
          if (contact === undefined) {
            throw new Error(`Cannot create claim communication: contact ${contactId} could not be fetched`);
          }
          communication = await createClaimCommunication(contact);
        }
        callContact(communication);
        communicationCreatedRef.current = true;
      } catch (error) {
        await reportAxiosError(error);
        handleCallFinished(); // We want to initialize callInProgress to enable receiving new calls
      }
    }

    const initializeCallCenter = async () => {
      const token = await fetchAccessToken();
      const device = new Device(token, { tokenRefreshMs: TOKEN_REFRESH_MS });

      attachTokenRefreshHandler(device, fetchAccessToken);
      device.on('registered', () => handleReady(device));
      device.on('unregistered', handleOffline);
      device.on('incoming', handleIncomingCall);
      device.on('error', function (error) {
        if (TWILIO_ERRORS_TO_IGNORE.includes(error.code)) {
          return;
        }

        reportTwilioError(error);
      });

      // Register the device to start receiving events
      await device.register();
    };

    initializeCallCenter();
  }, [
    contactId,
    claimId,
    phoneNumber,
    user.id,
    contactPhoneId,
    content,
    summary,
    exposureIds,
    categoryId,
    isNotClaimRelated,
    handleDisconnectOrCancel,
    handleCallAccepted,
    handleCallFinished,
    handleOffline,
    updateCallConnection,
    updateCommunication,
    userOrganization.id,
  ]);

  useEffect(() => {
    async function updateClaimCommunication() {
      try {
        const res = await axios.get(`/api/v1/claims/${claimToAttachId}/communications/${communicationId}`);
        updateCommunication(res.data);
      } catch (error) {
        await reportAxiosError(error);
      }
    }

    if (!claimToAttachId) {
      return;
    }

    updateClaimCommunication();
  }, [claimToAttachId, communicationId, updateCommunication]);

  if (isEmpty(communication) || isNil(communication) || !callStatus || isLoadingClaim || isErrorLoadingClaim) {
    return <LoadingDialog track="Initiate outgoing phone call" isError={isErrorLoadingClaim} />;
  }

  const callInProgressCardComponent = (
    <CallInProgressCard isIncoming={false} onCallHandledAndFinished={handleCallFinished} shouldShowClive={true} />
  );

  return claim ? (
    <ClaimContextProvider claim={claim} refreshData={reloadClaim} createMinimizedContext={false}>
      {callInProgressCardComponent}
    </ClaimContextProvider>
  ) : (
    callInProgressCardComponent
  );
};

export default OutgoingCallInProgressContainer;
